// CashieringWebSocketInitializer.tsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { FeedActionContext } from './FeedState';
import { useStateSelector } from '../global/dataSelectors';
import { useDispatcher } from '../global/hooks';
import { createSelector } from 'reselect';
import { DataState } from '../global/state';
import { useCashieringFeedStatus, useCashieringWebSocketCloseReason } from './FeedSelectors';
import { Websockets } from '../../constants';
import _ from 'lodash';
import { cashieringWebSocketService } from './CashieringWebSocketService';

const canConnectToWebsocket = createSelector(
  (s: DataState) => s.auth,
  auth => (auth.isExpired !== true && auth.isAuthenticated === true ? true : false)
);

const CashieringWebSocketInitializer: React.FC = () => {
  const dispatch = useContext(FeedActionContext);
  const [shouldReconnect, setShouldReconnect] = useState(false);
  const retryTimeoutMs = useRef<number>(250);
  const retryTimeout = useRef<NodeJS.Timeout>();
  const dispatchers = useDispatcher();
  const dispatcher = dispatchers.dispatcher.cashieringFeed;
  const globalDispatch = dispatchers.dispatch;
  const cashieringStatusFeedStatus = useCashieringFeedStatus();
  const closeReason = useCashieringWebSocketCloseReason();
  const canConnect = useStateSelector(canConnectToWebsocket);
  const apiToken = useStateSelector(state => state.auth.apiToken);

  useEffect(() => {
    return () => {
      // Clean up WebSocket on unmount if needed
      cashieringWebSocketService.closeConnection();
    };
  }, []);

  useEffect(() => {
    if (shouldReconnect && apiToken) {
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
      retryTimeout.current = setTimeout(() => {
        if (cashieringWebSocketService.shouldTryReconnect()) {
          cashieringWebSocketService
            .connect(dispatch, dispatcher, globalDispatch, apiToken)
            .then(() => setShouldReconnect(false));
        }
      }, retryTimeoutMs.current);
    }
  }, [shouldReconnect, apiToken]);

  useEffect(() => {
    if (cashieringStatusFeedStatus === 'closed') {
      if (canConnect) {
        const newTimeout = Math.min(Websockets.ReconnectInterval, retryTimeoutMs.current + retryTimeoutMs.current);
        const newTimeoutDisplay = _.round(newTimeout / 1000, 2);
        const closeMessage = `Cashiering Socket is closed. Reconnect will be attempted in ${newTimeoutDisplay} second.`;
        dispatcher.closed({
          reason: closeReason || '',
          retryingInSeconds: newTimeoutDisplay,
          message: closeMessage,
        });
        retryTimeoutMs.current = newTimeout;
        console.warn(closeMessage);
        setShouldReconnect(true);
      } else {
        const closeMessage = 'Cashiering is closed because user can no longer connect';
        dispatcher.closed({ reason: closeReason || '', message: closeMessage });
        console.warn(closeMessage);
      }
    }
  }, [cashieringStatusFeedStatus, closeReason, canConnect]);

  useEffect(() => {
    if (canConnect && apiToken) {
      console.log('initializing Cashiering websocket (this should only happen once!)');
      cashieringWebSocketService.connect(dispatch, dispatcher, globalDispatch, apiToken);
    } else if (!canConnect && !cashieringWebSocketService.shouldTryReconnect()) {
      console.log('disconnecting Cashiering websocket because user can no longer connect');
      cashieringWebSocketService.closeConnection();
    } else {
      console.log('doing nothing');
    }
  }, [dispatcher, canConnect, apiToken]);

  return null; // This component does not render anything
};

export default CashieringWebSocketInitializer;
